import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { fetchLocationLotOffer } from "features/lotOffer/fetchLotOffer";
import { LotOfferLocationInterface } from "interfaces/LotOfferLocationInterface";
import { fetchValidate, postPurchase, ValidateArticle } from "./fetchValidate";

export enum ShoppingCartInvalidStatus {
  TAKEN = "taken_lot_offer_ids",
  OVERFLOW = "overflown_lot_offer_ids",
}

export interface shoppingCartState {
  lotLocations: LotOfferLocationInterface[];
  articles: ValidateArticle[];
  invalid: {
    [key: string]: ShoppingCartInvalidStatus;
  };
  fromShoppingCart: any,
}

const initialState: shoppingCartState = {
  articles: [],
  lotLocations: [],
  invalid: {},
  fromShoppingCart: false,
};

const mergeArticles = (newArticles: ValidateArticle[], existingArticles: ValidateArticle[]) => {
  const mergedArticles = [...existingArticles];
  newArticles.forEach((newArticle) => {
    const existingArticleIndex = mergedArticles.findIndex((existingArticle) => existingArticle.id === newArticle.id);

    if (existingArticleIndex !== -1) {
      mergedArticles[existingArticleIndex] = newArticle;
    } else {
      mergedArticles.push(newArticle);
    }
  });
  return mergedArticles;
};

export const updatePrices = createAsyncThunk("shoppingCart/updatePrices", async (args, { getState }) => {
  const { shoppingCart, user } = getState() as RootState;
  const lot_offers = shoppingCart.articles.map((article) => fetchLocationLotOffer({ id: article.id, endpoint: user.role }));
  return Promise.allSettled(lot_offers).then((responses) => {
    const result: { article: ValidateArticle[], lot_offers: LotOfferLocationInterface[] } = { article: [], lot_offers: [] };
    responses.forEach((response) => {
      if (response.status === "fulfilled") {
        const { data } = response.value;
        if (data) {
          result.lot_offers.push(data.data);
          result.article.push({
            id: data.data.id,
            price: data.data.price,
            vat_rate: data.data.vat_rate,
            full_lot_number: data.data.full_lot_number,
          });
        }
      }
    });
    return result;
  }
  );
});

export const checkShoppingCart = createAsyncThunk("shoppingCart/validate", async (articles: ValidateArticle[], { getState }) => {
  const { shoppingCart } = getState() as RootState;
  const response = await fetchValidate({ articles: mergeArticles(articles, shoppingCart.articles), shopping_cart_validation: true });
  return { headers: response.headers, data: response.data, status: response.status };
});

export const checkAddToShoppingCart = createAsyncThunk("shoppingCart/validate", async (articles: ValidateArticle[], { getState }) => {
  const { shoppingCart } = getState() as RootState;
  const response = await fetchValidate({ articles: mergeArticles(articles, shoppingCart.articles), shopping_cart_validation: false });
  return { headers: response.headers, data: response.data, status: response.status };
});

export const purchaseShoppingCart = createAsyncThunk("shoppingCart/purchase", async (articles: ValidateArticle[], { getState }) => {
  const { shoppingCart } = getState() as RootState;
  const response = await postPurchase({ articles: mergeArticles(shoppingCart.articles, articles) });
  return { headers: response.headers, data: response.data, status: response.status };
});

export const shoppingCartSlice = createSlice({
  name: "shoppingCart",
  initialState,
  reducers: {
    clear: (state) => {
      return initialState;
    },
    clearShoppingCart: () => {
      return initialState;
    },
    deleteItemFromShoppingCart: (state, action: PayloadAction<LotOfferLocationInterface[]>) => {
      state.lotLocations = action.payload || [];
    },
    deleteFromShoppingIds: (state, action: PayloadAction<string>) => {
      state.articles = state.articles.filter((article) => article.id !== action.payload);
    },
    setShoppingCart: (state, action: PayloadAction<LotOfferLocationInterface>) => {
      if (state.lotLocations.findIndex((lotLocation) => lotLocation.id === action.payload.id) === -1) {
        state.articles = [...state.articles].concat([{ 
          id: action.payload.id, 
          price: action.payload.price, 
          vat_rate: action.payload.vat_rate,
          full_lot_number: action.payload.full_lot_number
        }]);
        state.lotLocations = [...state.lotLocations].concat(action.payload)
      }
    },
    setFromShoppingCart: (state, action: PayloadAction<boolean>) => {
      state.fromShoppingCart = action.payload;
    },

  },
  extraReducers: (builder) => {
    builder.addCase(updatePrices.fulfilled, (state, action) => {
      state.articles = action.payload.article;
      state.lotLocations = action.payload.lot_offers;
    });
    builder.addCase(checkShoppingCart.fulfilled, (state, action) => {
      if (action.payload.data.data) {
        action.payload.data.data.errors?.forEach((e) => {
          if (e.error.includes("Verkaufsmenge") && e.id) {
            const id = e.id;
            state.invalid = { ...state.invalid, [id]: ShoppingCartInvalidStatus.OVERFLOW }
          }
        });
        action.payload.data.data.errors?.forEach((e) => {
          if (e.error.includes("verkauft") && e.id) {
            const id = e.id;
            state.invalid = { ...state.invalid, [id]: ShoppingCartInvalidStatus.TAKEN }
          }
        });
      } else {
        state.invalid = {};
      }
    });
    builder.addCase(purchaseShoppingCart.fulfilled, (state, action) => {
      if (action.payload.data.data) {
        // action.payload.data.data.overflown_lot_offer_ids?.forEach((id) => {
        //   state.invalid = { ...state.invalid, id: ShoppingCartInvalidStatus.OVERFLOW }
        // });
        // action.payload.data.data.taken_lot_offer_ids?.forEach((id) => {
        //   state.invalid = { ...state.invalid, id: ShoppingCartInvalidStatus.TAKEN }
        // });
      } else {
        state.invalid = {};
      }
    });
  },
});

export const { clear, clearShoppingCart, setShoppingCart, deleteItemFromShoppingCart, deleteFromShoppingIds, setFromShoppingCart } = shoppingCartSlice.actions;

export const selectShoppingCartArticles = (state: RootState) => state.shoppingCart.articles;
export const selectShoppingCartIds = createSelector([selectShoppingCartArticles], (articles) => articles?.map((article) => article.id) ?? [])

export const selectShoppingCartLots = (state: RootState) => state.shoppingCart.lotLocations;
export const selectShoppingCartSize = (state: RootState) => state.shoppingCart.lotLocations.length;
export const selectShoppingCartInvalidIds = (state: RootState) => state.shoppingCart.invalid;
export const fromShoppingCart = (state: RootState) => state.shoppingCart.fromShoppingCart as any;


export default shoppingCartSlice.reducer;
